import type { ListUsersQuery, UserWithLabels } from '@clockwise/clients/users';
import { ApiError, LifecycleStatus } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Download, Filter, Pencil, Plus, Upload, UserCog } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { BusinessUnitFilter } from '@/components/custom/business-unit-filter';
import { CustomFieldFilter } from '@/components/custom/custom-field-filter';
import { DataTable } from '@/components/custom/data-table';
import { LifecycleStatusBadge } from '@/components/custom/lifecycle-status-badge';
import { PersonUnitsCell } from '@/components/custom/person-units-cell';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActionSpacer, RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { UserFormSheet } from '@/components/custom/user-form-sheet';
import { UserImportSheet } from '@/components/custom/user-import-sheet';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usersApi } from '@/lib/api';
import { useCanIn, useShowsUnits } from '@/lib/business-units';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';
import { userDisplayName, userStatus } from '@/lib/user-display';

const SORT_FIELD_VALUES = [
  'firstName',
  'lastName',
  'email',
  'title',
  'preferredName',
  'startDate',
  'terminatedDate',
  'createdAt',
  'updatedAt',
] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  customFieldId: z.string().uuid().optional(),
  customFieldValue: z.string().optional(),
  /** One business unit (from a unit's page, or the filter). */
  unit: z.string().uuid().optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/users/')({
  validateSearch: listSearch,
  component: UsersPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'firstName';

function UsersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { user: me, permissions } = useMe();
  const queryClient = useQueryClient();
  const canImpersonate = permissions.has('user.impersonate');
  const canEditUsers = permissions.has('user.edit');
  const canIn = useCanIn();
  const showsUnits = useShowsUnits('user.view');

  const startImpersonation = useMutation({
    mutationFn: (userId: string) => usersApi.startImpersonation(userId),
    onSuccess: () => {
      // Full reload so cached per-user data (queries keyed by previous
      // identity) doesn't bleed across the identity boundary. Cheaper
      // than enumerating every query to invalidate, and matches user
      // mental model: "I'm now a different person."
      queryClient.clear();
      window.location.assign('/');
    },
  });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';
  const customFieldId = search.customFieldId ?? '';
  const customFieldValue = search.customFieldValue ?? '';

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  const [importOpen, setImportOpen] = useState(false);
  // `null` = closed, `{}` = the Add flow, `{ id }` = editing that row — one
  // sheet serves both, mirroring `customers.index.tsx`.
  const [formSheet, setFormSheet] = useState<{ id?: string } | null>(null);

  const exportMutation = useMutation({
    // Mirror the on-screen filters so the file matches the visible list.
    mutationFn: () =>
      usersApi.exportXlsx({
        q: q.trim() || undefined,
        active: status === 'all' ? undefined : status === 'active',
        customFieldId: customFieldId && customFieldValue ? customFieldId : undefined,
        customFieldValue: customFieldId && customFieldValue ? customFieldValue : undefined,
        sortField,
        sortDirection,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'employees.xlsx');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Export failed');
    },
  });

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListUsersQuery = {
    q: q.trim() || undefined,
    businessUnitId: search.unit,
    active: status === 'all' ? undefined : status === 'active',
    sortField,
    sortDirection,
    customFieldId: customFieldId && customFieldValue ? customFieldId : undefined,
    customFieldValue: customFieldId && customFieldValue ? customFieldValue : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.users.list(listQuery),
    queryFn: () => usersApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  // System/service role, department, and manager names are resolved
  // server-side and arrive on each row (`systemRoleName` etc.). This
  // replaces three capped (`limit: 200`) catalog fetches joined by id —
  // which silently blanked any name past row 200 — plus an N+1 fan-out of
  // one request per distinct manager. A `null` label means the referenced
  // entity was deleted; the cell renders "—".

  const columns = useMemo<ColumnDef<UserWithLabels>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: 'Name',
        size: 160,
        cell: ({ row }) => (
          <span className="block font-medium break-words">{userDisplayName(row.original)}</span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 200,
        cell: ({ row }) => <span className="block text-sm break-all">{row.original.email}</span>,
      },
      {
        accessorKey: 'title',
        header: 'Title',
        size: 120,
        cell: ({ row }) =>
          row.original.title ? (
            <span className="block text-sm break-words">{row.original.title}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      // Where they work — shown once the caller works in more than one unit.
      ...(showsUnits
        ? [
            {
              id: 'businessUnit',
              header: 'Business Unit',
              size: 130,
              enableSorting: false,
              cell: ({ row }: { row: { original: UserWithLabels } }) => (
                <PersonUnitsCell units={row.original.businessUnits} />
              ),
            } satisfies ColumnDef<UserWithLabels>,
          ]
        : []),
      {
        id: 'systemRole',
        header: 'System role',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="block text-sm break-words">{row.original.systemRoleName ?? '—'}</span>
        ),
      },
      {
        id: 'serviceRole',
        header: 'Service role',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="block text-sm break-words">{row.original.serviceRoleName ?? '—'}</span>
        ),
      },
      {
        id: 'department',
        header: 'Department',
        size: 110,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.departmentName ? (
            <span className="block text-sm break-words">{row.original.departmentName}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'manager',
        header: 'Manager',
        size: 130,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.managerName ? (
            <span className="block text-sm break-words">{row.original.managerName}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => {
          const s = userStatus(row.original);
          return (
            <LifecycleStatusBadge
              status={s === 'Active' ? LifecycleStatus.Active : LifecycleStatus.Inactive}
            />
          );
        },
      },
      // Present when the caller can act on a row at all — edit, impersonate,
      // or both. Omitted otherwise so a read-only caller doesn't lose width to
      // a permanently empty column.
      //
      // Presence-only gate on the pencil: `user.edit` is scoped (all /
      // my_team) but `useHasPermission` reads presence, not scope, so a
      // scoped caller sees the pencil on employees outside their scope and the
      // server rejects the save. The DETAIL page doesn't have this problem — it
      // keys off the server-derived `related.editable` from GET /users/:id.
      // Closing it here needs the same flag on the list response.
      ...(canImpersonate || canEditUsers
        ? [
            {
              id: 'actions',
              header: '',
              size: 96,
              enableSorting: false,
              cell: ({ row }) => {
                const u = row.original;
                // Impersonation needs an existing BetterAuth identity, so
                // hide it for inactive users, self, and a null baUserId
                // (which always 403s).
                const showImpersonate =
                  canImpersonate && u.active && u.id !== me?.id && !!u.baUserId;
                return (
                  <RowActions>
                    {/* Only on employees of a unit whose role (theirs there)
                        edits employees — their HR record is their employer's. */}
                    {canIn('user.edit', u.businessUnitId) ? (
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Edit ${userDisplayName(u)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormSheet({ id: u.id });
                        }}
                      >
                        <Pencil />
                      </Button>
                    ) : null}
                    {/* Impersonation is the only row action besides Edit, so it
                        sits inline instead of behind a kebab. Rows that can't be
                        impersonated keep the slot so the pencils stay in one
                        vertical lane. */}
                    {showImpersonate ? (
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Impersonate ${userDisplayName(u)}`}
                        title="Impersonate"
                        disabled={startImpersonation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          startImpersonation.mutate(u.id);
                        }}
                      >
                        <UserCog />
                      </Button>
                    ) : (
                      <RowActionSpacer />
                    )}
                  </RowActions>
                );
              },
            } satisfies ColumnDef<UserWithLabels>,
          ]
        : []),
    ],
    [canEditUsers, canImpersonate, me?.id, startImpersonation, showsUnits, canIn],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader title="Employees" subtitle="Employees and their roles." count={total}>
        <RequirePermission permission="user.view">
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </RequirePermission>
        <RequirePermission permission="user.edit">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import
          </Button>
        </RequirePermission>
        <RequirePermission permission="user.edit">
          <Button onClick={() => setFormSheet({})}>
            <Plus />
            Add Employee
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search name, email, location…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <BusinessUnitFilter
          value={search.unit}
          onChange={(unit) => updateSearch({ unit, page: 0 })}
          permission="user.view"
        />
        <CustomFieldFilter
          object="user"
          fieldId={customFieldId}
          value={customFieldValue}
          onChange={(id, val) =>
            updateSearch({
              customFieldId: id || undefined,
              customFieldValue: val || undefined,
              page: 0,
            })
          }
        />
      </div>

      <DataTable
        table={table}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Failed to load users.'
        }
        emptyMessage={
          q ? 'No employees match this search.' : 'No employees yet. Click “Add Employee”.'
        }
        onRowClick={(u) => navigate({ to: '/users/$id', params: { id: u.id } })}
        skeletonRows={5}
      />

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <UserImportSheet open={importOpen} onOpenChange={setImportOpen} />
      <UserFormSheet
        open={formSheet !== null}
        mode={formSheet?.id ? 'edit' : 'create'}
        userId={formSheet?.id}
        onOpenChange={(o) => {
          if (!o) setFormSheet(null);
        }}
      />
    </PageShell>
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.unit) out.unit = s.unit;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  if (s.customFieldId && s.customFieldValue) {
    out.customFieldId = s.customFieldId;
    out.customFieldValue = s.customFieldValue;
  }
  return out;
}
